/**
 * "Glossary" — Introduction section (ADR-0074).
 * Structure + `docs.*` keys only; prose lives in the per-locale catalogs.
 * The cross-cutting vocabulary used throughout the docs; later sections define
 * their own domain terms inline and may add entries here.
 */
import type { DocPageContent } from '../types';

const TERM = (k: string) => ({
  termKey: `docs.glossary.term.${k}.term`,
  descKey: `docs.glossary.term.${k}.desc`,
});

export const GLOSSARY_PAGE: DocPageContent = {
  slug: 'glossary',
  titleKey: 'docs.glossary.title',
  descriptionKey: 'docs.glossary.description',
  blocks: [
    { kind: 'paragraph', textKey: 'docs.glossary.lead' },
    {
      kind: 'definitions',
      items: [
        TERM('capability'),
        TERM('catalog'),
        TERM('discovery'),
        TERM('sideEffect'),
        TERM('widget'),
        TERM('chatSurface'),
        TERM('grounding'),
        // "Approved websites" (ADR-0443) sits next to `grounding` because it is
        // the only outbound half of it — and it gets an entry rather than an
        // exemption for the reason the entry itself states: the label collides
        // with widget ORIGINS, which govern the opposite direction (who may
        // embed the widget), and a reader has no way to tell them apart from
        // two everyday words.
        TERM('allowedSites'),
        // "Site mapping" sits at the head of that cluster because it is the ACT
        // the other two are products of: the manual describes what a site has,
        // a guide is a route through it, and mapping is Syncanix reading the
        // site to learn either. It gets an entry rather than an exemption on
        // the `allowedSites` criterion, and the collision is nearer than that
        // one: "site map" already means a sitemap to any reader — the list of
        // addresses a site publishes for search engines — and the sidebar
        // carries a second "Map" a row away (`interactionMap`), which is a
        // different thing again. Two everyday words, three readings.
        TERM('siteMapping'),
        // "Site manual" (WS-C, widget-surface-mapping) also sits in the
        // grounding cluster: it is the grounding SOURCE a zero-docs workspace
        // gets — a generated description of the site itself, assembled from
        // discovery — and "manual" at face value suggests a human-written
        // document, which is exactly what it is not.
        TERM('siteManual'),
        // "Task guides" sits directly under the site manual, and the adjacency is
        // the definition: the manual describes what a site HAS, a guide is a route
        // THROUGH it. Both are assembled from the same discovery and both are
        // operator-curated, so a reader meeting one needs the other to tell them
        // apart. It gets an entry rather than an exemption for the manual's own
        // reason — at face value "task guides" reads as written how-to articles,
        // which is exactly what they are not.
        TERM('taskGuides'),
        // "Engagement" (the /engagement merge of chat starters, proactive
        // nudges and page rules) gets an entry rather than an exemption for the
        // same reason `allowedSites` does: two everyday words that a reader
        // will land on with the wrong meaning. Every other analytics surface in
        // the product measures engagement, so the bare label reads as a metrics
        // page — where it is the opposite, the place the outreach itself is
        // CONFIGURED. Placed after the chat-surface cluster it belongs to.
        // ONE entry, deliberately. The on-page-experiences branch added a second
        // `TERM('engagement')` for "engagement rule" — the v2 engine's unit of
        // governance — and the merge kept both, so the page rendered this same
        // definition twice under a duplicate React key. The rule is not a
        // separate glossary noun for the reader: after the merge it is the
        // Rules TAB of the page this entry already defines, so a second term
        // would define a tab rather than a concept. Re-adding one needs its own
        // id, its own copy in six catalogs, and a sidebar row to map to.
        TERM('engagement'),
        TERM('mcp'),
        TERM('mcpServer'),
        TERM('builder'),
        TERM('endUser'),
        TERM('userLearning'),
        TERM('agentImpact'),
        // `interaction` is the unit plans are SHOPPED in, and it sits next to
        // `interactionMap`, an unrelated analytics surface, deliberately: the
        // two nouns collide in the product and a buyer has no way to tell them
        // apart without the adjacency.
        TERM('interaction'),
        TERM('interactionMap'),
        TERM('idp'),
        TERM('federation'),
        TERM('jwt'),
        TERM('jwks'),
        TERM('oidc'),
        TERM('pkce'),
        TERM('scope'),
        TERM('tokenExchange'),
        TERM('bff'),
        TERM('stepUp'),
        TERM('actOnBehalf'),
        TERM('confirmationTiers'),
        TERM('rowLevelSecurity'),
        TERM('signedIntent'),
        TERM('evaluationGate'),
        TERM('drift'),
        TERM('zdr'),
        TERM('byok'),
        TERM('byomcp'),
        TERM('witness'),
        TERM('usageCredits'),
        TERM('environments'),
        TERM('promote'),
        TERM('keys'),
        TERM('workspace'),
      ],
    },
  ],
};
